import React from 'react';
import { sponsors } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const Sponsors = () => {
  const { mainSponsors, newPartners2026 } = sponsors;
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section with B&W background, colored subject, and vignette */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        {/* B&W Background Layer */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_11a5c023-0086-4456-b2f2-508c2ca946e7/artifacts/809y9ioe_94006F2B-B567-4D42-8FB3-1A7729AE1348.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center 30%',
            filter: 'grayscale(100%)'
          }}
        ></div>
        
        {/* Colored Subject Layer (center mask) */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_11a5c023-0086-4456-b2f2-508c2ca946e7/artifacts/809y9ioe_94006F2B-B567-4D42-8FB3-1A7729AE1348.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center 30%',
            maskImage: 'radial-gradient(ellipse 45% 80% at 50% 50%, black 30%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse 45% 80% at 50% 50%, black 30%, transparent 70%)'
          }}
        ></div>
        
        {/* Vignette Effect */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 20%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.85) 100%)'
          }}
        ></div>
        
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/30"></div>
        
        {/* Content */}
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">{t('ourSponsors')}</h1>
          <p className="text-xl md:text-2xl font-light text-cyan-400">{t('partnersInExcellence')}</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-neutral-950">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <p className="text-xl text-gray-300 leading-relaxed">
            {t('sponsorsIntro')}
          </p>
        </div>
      </section>

      {/* Main Sponsors */}
      <section className="py-20 bg-black border-t border-cyan-400/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">{t('mainSponsors')}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-pink-500 to-cyan-400 mx-auto"></div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {mainSponsors.map((sponsor) => (
              <a
                key={sponsor.id}
                href={sponsor.website || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-neutral-900/50 border border-neutral-800 hover:border-pink-500/50 p-4 transition-all duration-300 flex flex-col items-center justify-center text-center hover:bg-neutral-900 rounded-lg"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-cyan-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
                <div className="relative z-10 flex flex-col items-center justify-center w-full">
                  {/* Logo Image */}
                  <div className="w-full h-32 flex items-center justify-center mb-3 overflow-hidden">
                    <img 
                      src={sponsor.logo} 
                      alt={`${sponsor.name} logo`}
                      className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-110"
                    />
                  </div>
                  {/* Description */}
                  <p className="text-xs text-zinc-500 group-hover:text-zinc-400 transition-colors line-clamp-2 mt-2">{sponsor.description}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* New Partners 2026 */}
      <section className="py-20 bg-neutral-950 border-t border-pink-500/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-pink-500 to-cyan-400 text-black px-6 py-2 text-sm font-bold tracking-wider mb-6">
              {t('new')}
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">{t('newPartners')}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-pink-500 mx-auto"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {newPartners2026.map((sponsor) => (
              <a
                key={sponsor.id}
                href={sponsor.website || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-gradient-to-br from-neutral-900 to-neutral-950 border-2 border-cyan-400/30 hover:border-cyan-400 p-6 transition-all duration-300 flex flex-col items-center justify-center text-center rounded-xl"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-t-xl"></div>
                <div className="relative z-10 flex flex-col items-center justify-center w-full">
                  {/* Logo Image */}
                  <div className="w-full h-40 flex items-center justify-center mb-4 overflow-hidden">
                    <img 
                      src={sponsor.logo} 
                      alt={`${sponsor.name} logo`}
                      className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-110"
                    />
                  </div>
                  {/* Description */}
                  <p className="text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors">{sponsor.description}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership CTA */}
      <section className="py-20 bg-black border-t border-zinc-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">{t('interestedInPartnership')}</h2>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            {t('partnershipCTA')}
          </p>
          <a
            href="mailto:g.bassirider@icloud.com"
            className="inline-block bg-gradient-to-r from-pink-500 to-cyan-400 text-black px-10 py-4 font-bold text-lg hover:from-cyan-400 hover:to-pink-500 transition-all duration-300 transform hover:scale-105"
          >
            {t('contactMe')}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Sponsors;
